import { ReactNode } from "react";
import { BookOpen, FileText, List, Video, Clock, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { RevealBlock } from "./ScrollRevealSection";

interface Section {
  id: string;
  title: string;
  activities?: { id: string; activity_type: string; duration_minutes?: number }[];
}

interface Activity {
  id: string;
  title: string;
  activity_type: string;
  duration_minutes?: number;
}

interface ModuleOverviewHeroProps {
  title: string;
  description?: string;
  sections: Section[];
  moduleActivities: Activity[];
  moduleNumber?: number;
}

export function ModuleOverviewHero({
  title,
  description,
  sections,
  moduleActivities,
  moduleNumber
}: ModuleOverviewHeroProps) {
  // Calculate total duration
  const totalDuration = [
    ...sections.flatMap(s => s.activities || []),
    ...moduleActivities
  ].reduce((sum, a) => sum + (a.duration_minutes || 0), 0);

  const totalItems = sections.length + moduleActivities.length;

  return (
    <div className="space-y-8 py-6">
      {/* Hero Section */}
      <RevealBlock>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-6 sm:p-8">
          {/* Background decorations */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
              {/* Module icon */}
              <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/20 border border-primary/30 shrink-0">
                <BookOpen className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
              </div>

              <div className="flex-1 space-y-3">
                {moduleNumber && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary">
                    Module {moduleNumber}
                  </span>
                )}
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
                  {title}
                </h1>
                {description && (
                  <p className="text-muted-foreground text-base sm:text-lg leading-relaxed max-w-2xl">
                    {description}
                  </p>
                )}
              </div>
            </div>

            {/* Quick stats */}
            <div className="flex flex-wrap gap-4 sm:gap-6 mt-6 pt-6 border-t border-primary/20">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Layers className="h-4 w-4 text-primary" />
                <span className="text-sm">{totalItems} sections</span>
              </div>
              {totalDuration > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-sm">{totalDuration} min</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </RevealBlock>

      {/* Content preview cards */}
      {(sections.length > 0 || moduleActivities.length > 0) && (
        <div className="space-y-4">
          <RevealBlock delay={100}>
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
              What you'll learn
            </h3>
          </RevealBlock>

          <div className="grid gap-3">
            {sections.map((section, idx) => (
              <RevealBlock key={section.id} delay={150 + idx * 80}>
                <div className="group flex items-center gap-4 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/30 hover:bg-muted/30 transition-all duration-300">
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{section.title}</p>
                    {section.activities && section.activities.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {section.activities.length} {section.activities.length === 1 ? 'activity' : 'activities'}
                      </p>
                    )}
                  </div>
                  <div className="text-muted-foreground/50 group-hover:text-primary/50 transition-colors">
                    <span className="text-sm font-medium">{idx + 1}</span>
                  </div>
                </div>
              </RevealBlock>
            ))}

            {moduleActivities.map((activity, idx) => (
              <RevealBlock key={activity.id} delay={150 + (sections.length + idx) * 80}>
                <div className="group flex items-center gap-4 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/30 hover:bg-muted/30 transition-all duration-300">
                  <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                    {activity.activity_type === 'video' ? (
                      <Video className="h-5 w-5" />
                    ) : activity.activity_type === 'quiz' ? (
                      <List className="h-5 w-5" />
                    ) : (
                      <FileText className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">{activity.title}</p>
                    <p className="text-xs text-muted-foreground capitalize mt-0.5">
                      {activity.activity_type}
                      {activity.duration_minutes && activity.duration_minutes > 0 && (
                        <span> • {activity.duration_minutes} min</span>
                      )}
                    </p>
                  </div>
                </div>
              </RevealBlock>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {sections.length === 0 && moduleActivities.length === 0 && (
        <RevealBlock delay={200}>
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm italic">No content in this module yet</p>
          </div>
        </RevealBlock>
      )}
    </div>
  );
}
